import { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Image as ImageIcon, Volume2, ArrowRight, Upload } from 'lucide-react';
import configData from "../config.json";
import { getToken, removeToken } from "../utils/auth";

import { fetchBannerFile } from '../functions/generate_banner.jsx';
import { fetchAudioFile } from '../functions/generate_audio_file.jsx';
import { fetchAudioText } from '../functions/generate_audio_text.jsx';
import { fetchVideoFile } from '../functions/generate_video.jsx';
import { uploadToMediaManagement } from '../functions/upload_to_MM.jsx';
import { uploadImageToBucket } from '../functions/upload_image_to_bucket.jsx';
import { fetchWholeAudio } from '../functions/generate_whole_audio.jsx';
import { editBannerFile } from '../functions/edit_banner.jsx';

export function  Step2Generate ({ 
  marketingGoals,
  targetAudience,
  vibeAndTone,
  setCurrentBalance,
  AutoResizeTextarea,
  fetchAudioFileStatus,
  spinning,
  setSpinning,
  fetchAudioTextStatus,
  fetchonlyAudioStatus,
  fetchBannerFileStatus,
  fetchVideoFileStatus,
  ChangeFetchAudioFileStatus,
  setFetchAudioTextStatus,
  setFetchonlyAudioStatus,
  ChangeFetchBannerFileStatus, 
  ChangeFetchVideoFileStatus,
  defaultCampaigns,
  language,
  audioData, 
  bannerData,
  audioUrl,
  setAudioUrl,
  audioText,
  setAudioText,
  bannerUrl,
  setBannerUrl,
  videoUrl,
  setVideoUrl,
  animateBanner,
  setAnimateBanner,
  aspectBannerRatio,
  setAspectBannerRatio,
  aspectVideoRatio,
  setAspectVideoRatio,
  templateBanner,
  setTemplateBanner,
  selectedCategory,
  setSelectedCategory,
  setUploadBannerID,
  setUploadAudioID,
  setUploadVideoID,
  productImageUrl,
  setProductImageUrl,
  campaignDescription,
  setCampaignDescription,
  allGenratedBanners,
  setAllGenratedBanners,
  allGenratedVideos,
  setAllGenratedVideos,
  allGenratedAudios,
  setAllGenratedAudios,
  allUploadedAudios,
  setAllUploadedAudios,
  selectedBanner,
  setSelectedBanner,
  selectedVideo,
  setSelectedVideo,
  selectedAudio,
  setSelectedAudio,
  onDeploy, 
  FadeIn, 
  AssetCard,
  notify}) {

  const textareaRef = useRef(null);
  const campaignDropdownRef = useRef(null);
  const productImageFileInputRef = useRef(null);
  const navigate = useNavigate();

  const autoResize = (el) => {
    el.style.height = "auto"; // reset height
    el.style.height = Math.max(el.scrollHeight + 10, 24) + "px"; // set height to scrollHeight
  };

  // optional: auto-resize on mount if there is initial text
  useEffect(() => {
    if (textareaRef.current) autoResize(textareaRef.current);
  }, []);

  const [manualLoading, setManualLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false)
  const [manualUrl, setManualUrl] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualFile, setManualFile] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualVideoLoading, setManualVideoLoading] = useState(false);
  const [manualVideoUrl, setManualVideoUrl] = useState("");
  const [manualVideoName, setManualVideoName] = useState("");
  const [manualVideoFile, setManualVideoFile] = useState(null);
  const [showManualVideoModal, setShowManualVideoModal] = useState(false);
  const [showEditBannerModal, setShowEditBannerModal] = useState(false);
  const [editBannerInstructions, setEditBannerInstructions] = useState("");
  const [editBannerLoading, setEditBannerLoading] = useState(false);
  const [campaignQuery, setCampaignQuery] = useState("");
  const [isCampaignDropdownOpen, setIsCampaignDropdownOpen] = useState(false);
  const [isProductImagePreviewValid, setIsProductImagePreviewValid] = useState(false);
  const [isUploadingProductImage, setIsUploadingProductImage] = useState(false);

  const [editableTextAudio, setEditableTextAudio] = useState(audioText || "");

  const campaignEntries = Object.entries(defaultCampaigns || {});
  const campaignOptions = [
    ...campaignEntries.map(([key, data]) => ({
      key,
      label: `${data?.Product_title || key} ${data?.Price || ""}`.trim(),
    })),
    { key: "manual", label: "Custom / Manual" },
  ];

  useEffect(() => {
    if (!campaignEntries.length || selectedCategory === "manual") {
      return;
    }

    const normalizedSelectedCategory = String(selectedCategory ?? "");
    const hasValidSelection = campaignEntries.some(
      ([campaignKey]) => campaignKey === normalizedSelectedCategory
    );

    // if (!hasValidSelection) {
    //   setSelectedCategory(campaignEntries[0][0]);
    // }
  }, [campaignEntries, selectedCategory, setSelectedCategory]);

  const normalizedCampaignQuery = campaignQuery.trim().toLowerCase();
  const filteredCampaignOptions = campaignOptions.filter((option) => {
    if (!normalizedCampaignQuery) {
      return true;
    }
    return `${option.label} ${option.key}`
      .toLowerCase()
      .includes(normalizedCampaignQuery);
  });

  useEffect(() => {
    if (selectedCategory === "manual") {
      setCampaignQuery("Custom / Manual");
      return;
    }

    const selectedCampaign = campaignOptions.find(
      (campaign) => campaign.key === selectedCategory
    );
    // setCampaignQuery(selectedCampaign?.label || "");
  }, [selectedCategory, defaultCampaigns]);

  useEffect(() => {
    if (!selectedCategory || selectedCategory === "manual") {
      return;
    }
    if(campaignQuery === ""){
      return;
    }

    const selectedCampaign = defaultCampaigns?.[selectedCategory];
    if (!selectedCampaign) {
      return;
    }
    const disclaimer = (selectedCampaign?.Disclaimer || "").trim();
    const description = (selectedCampaign?.Description || "").trim();
    const productTitle = selectedCampaign?.Product_title ? selectedCampaign.Product_title : "";
    const price = selectedCampaign?.Price ? selectedCampaign.Price : "";

    const campaignBody = [description, disclaimer].filter(Boolean).join("\n");
    const campaignText = `${productTitle}${price}.${campaignBody ? `\n${campaignBody}` : ""}`.trim();

    setCampaignDescription(campaignText);
    setProductImageUrl(selectedCampaign?.Product_image || "");
  }, [selectedCategory, defaultCampaigns, setCampaignDescription, setProductImageUrl]);

  useEffect(() => {
    setIsProductImagePreviewValid(false);
  }, [productImageUrl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(event.target)
      ) {
        setIsCampaignDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getAudioAssetUrl = (audio) => audio?.audioUrl || audio?.url || audio?.audio_url || "";
  const getAudioAssetText = (audio) => audio?.audioText || audio?.text || audio?.audio_text || "";

  const saveAudioBundle = ({ audioUrl: generatedAudioUrl, audioText: generatedAudioText, audio_id }) => {
    if (!generatedAudioUrl || !generatedAudioText) {
      return;
    }

    const fallbackId = `Audio_${new Date().toISOString()}`;
    const bundle = {
      audioUrl: generatedAudioUrl,
      audioText: generatedAudioText,
      audio_id: audio_id || fallbackId,
    };

    setAllGenratedAudios((prev = []) => {
      const key = bundle.audio_id || generatedAudioUrl;
      const withoutCurrent = prev.filter((item) => (item.audio_id || getAudioAssetUrl(item)) !== key);
      return [...withoutCurrent, bundle];
    });
    setSelectedAudio(bundle);
  };

  const isManualCampaignSelected = selectedCategory === "manual";
  const normalizedCampaignDescription = (campaignDescription || "").trim();
  const normalizedProductImageUrl = (productImageUrl || "").trim();
  const campaignDescriptionHeightClass = isProductImagePreviewValid
    ? "h-[160px]"
    : "h-[38px]";
  const campaignDescriptionFontClass = isProductImagePreviewValid
    ? "text-xl"
    : "text-sm";
  const effectiveCampaignDescription = isManualCampaignSelected ? normalizedCampaignDescription : "";
  const effectiveProductImageUrl = isManualCampaignSelected ? normalizedProductImageUrl : "";

  const validateManualCampaignInputs = (assetType) => {
    if (!isManualCampaignSelected) {
      return true;
    }

    if (!normalizedCampaignDescription) {
      notify("Campaign Description does not have any value.", "error");
      return false;
    }

    if ((assetType === "signage" || assetType === "video") && !normalizedProductImageUrl) {
      notify("Product Image URL does not have any value.", "error");
      return false;
    }

    return true;
  };

  const handleProductImageUpload = async (file) => {
    if (!(file instanceof File)) {
      return;
    }

    if (!file.type?.startsWith("image/")) {
      notify("Please choose a valid image file.", "error");
      return;
    }

    setIsUploadingProductImage(true);
    try {
      const token = getToken();
      const form = new FormData();
      form.append("file", file, file.name);

      const uploadName = file?.name ? file.name.replace(/\.[^/.]+$/, "") : "upload";
      form.append("name", uploadName);

      const response = await fetch(`${configData.DEMO_URL}/UploadImageToBucket`, {
        method: "POST",
        headers: {
          "x-access-token": token,
        },
        body: form,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          removeToken();
          navigate("/");
          return;
        }

        let errorMessage = `Failed to upload image (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage =
            errorData?.message ||
            errorData?.error ||
            errorData?.detail ||
            errorMessage;
        } catch (_) {}
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const uploadedUrl = responseData?.image_url;

      if (!uploadedUrl) {
        throw new Error("Image uploaded but no URL was returned.");
      }

      setProductImageUrl(uploadedUrl);
      setSelectedCategory("manual");
      setCampaignQuery("Custom / Manual");
      notify("Image uploaded successfully.", "success");
    } catch (error) {
      notify(error?.message || "Failed to upload image.", "error");
    } finally {
      setIsUploadingProductImage(false);
    }
  };






  const handleRegenerate = (id) => {
    if (!validateManualCampaignInputs(id)) {
      return;
    }

    setSpinning(prev => ({ ...prev, [id]: true }));
    switch(id) {
      case "audio":
        ChangeFetchAudioFileStatus(true);
        break;
      case "signage":
        ChangeFetchBannerFileStatus(true);
        break;
      case "video":
        ChangeFetchVideoFileStatus(true);
        break;
      default:
        break;
    }
  };


  
        // ------------------ text + audio Effects ------------------
    
  useEffect(() => {

      if (fetchAudioFileStatus === true) {
        if (!validateManualCampaignInputs("audio")) {
          ChangeFetchAudioFileStatus(false);
          setSpinning(prev => ({ ...prev, ["audio"]: false }));
          return;
        }
        ChangeFetchAudioFileStatus(false);
          const fetchAndUpload = async () => {
              try {
                  await fetchWholeAudio({
                    audioData,
                    defaultCampaigns,
                    selectedCategory,
                    campaignDescription: effectiveCampaignDescription,
                    language,
                    setEditableTextAudio,
                    setAudioText,
                    setCurrentBalance,
                    setSpinning,
                    setFetchAudioTextStatus,
                    setAllUploadedAudios,
                    getToken,
                    configData,
                    navigate,
                    notify,
                    marketingGoals,
                    targetAudience,
                    vibeAndTone,
                    setUploadAudioID,
                    setAudioUrl,
                    onAudioGenerated: saveAudioBundle,
                    ChangeFetchAudioFileStatus,
                    setFetchonlyAudioStatus,
                });
              } catch (error) {
                  notify("Error fetching or uploading audio:", "error");
                  setSpinning(prev => ({ ...prev, ["signage"]: false }));
              }
          };

          fetchAndUpload();
      }
  }, [fetchAudioFileStatus]);

          // ------------------ Fetch only audio text ------------------
    useEffect(() => {
      if (fetchAudioTextStatus === true) {
        if (!validateManualCampaignInputs("audio")) {
          setFetchAudioTextStatus(false);
          setSpinning(prev => ({ ...prev, ["audio_text"]: false }));
          return;
        }
        setFetchAudioTextStatus(false);
        setSpinning(prev => ({ ...prev, ["audio_text"]: true }));
        const fetchonlyText = async () => {
            try {
                await fetchAudioText({
                      audioData,
                      defaultCampaigns,
                      selectedCategory,
                      campaignDescription: effectiveCampaignDescription,
                      language,
                      setEditableTextAudio,
                      setAudioText,
                      setCurrentBalance,
                      setSpinning,
                      setFetchAudioTextStatus,
                      getToken,
                      configData,
                      navigate,
                      notify
                  });
                
            } catch (error) {
                notify("Error fetching or uploading audio:", "error");
                setSpinning(prev => ({ ...prev, ["signage"]: false }));
            }
        };
        fetchonlyText();
      }
  }, [fetchAudioTextStatus]);

  // ------------------ Fetch only audio ------------------

    useEffect(() => {
      if (fetchonlyAudioStatus === true) {
        setFetchonlyAudioStatus(false);
        setSpinning(prev => ({ ...prev, ["audio_only"]: true }));
        const fetchAudioFromText = async () => {
            try {
                  await fetchAudioFile({
                        fetchText: editableTextAudio,
                        audioData,
                        marketingGoals,
                        targetAudience,
                        vibeAndTone,
                        setUploadAudioID,
                        language,
                        setAudioUrl,
                        onAudioGenerated: saveAudioBundle,
                        setCurrentBalance,
                        setSpinning,
                        setAllUploadedAudios,
                        ChangeFetchAudioFileStatus,
                        setFetchonlyAudioStatus,
                        getToken,
                        configData,
                        navigate,
                        notify,
                      });
            } catch (error) {
                notify("Error fetching or uploading audio:", "error");
                setSpinning(prev => ({ ...prev, ["signage"]: false }));
            }
        };
        fetchAudioFromText();  }
  }, [fetchonlyAudioStatus]);

  
  
    // ------------------ Banner Effects ------------------


    useEffect(() => {
        if (fetchBannerFileStatus) {
            if (!validateManualCampaignInputs("signage")) {
              ChangeFetchBannerFileStatus(false);
              setSpinning(prev => ({ ...prev, ["signage"]: false }));
              return;
            }
            ChangeFetchBannerFileStatus(false);
            const fetchAndUpload = async () => {
              try {
              await fetchBannerFile({
                bannerData,
                defaultCampaigns,
                selectedCategory,
                campaignDescription: effectiveCampaignDescription,
                productImageUrl: effectiveProductImageUrl,
                language,
                marketingGoals,
                targetAudience,
                vibeAndTone,
                bannerUrl,
                setBannerUrl,
                setCurrentBalance,
                setUploadBannerID,
                setAllGenratedBanners,
                ChangeFetchBannerFileStatus,
                aspectBannerRatio,
                templateBanner,
                setSpinning,
                getToken,
                configData,
                navigate,
                notify
              });

              } catch (error) {
                notify("Error fetching or uploading banner:", "error");
                setSpinning(prev => ({ ...prev, ["signage"]: false }));
              }
            };
            fetchAndUpload();
        }
    }, [fetchBannerFileStatus]);



  


    // ------------------ Video Effects ------------------
    useEffect(() => {
        if (fetchVideoFileStatus) {
            if (!validateManualCampaignInputs("video")) {
              ChangeFetchVideoFileStatus(false);
              setSpinning(prev => ({ ...prev, ["video"]: false }));
              return;
            }
            ChangeFetchVideoFileStatus(false);
            const fetchAndUpload = async () => {
              try {
                await fetchVideoFile({
                  bannerData,
                  bannerUrl,
                  animateBanner,
                  defaultCampaigns,
                  marketingGoals,
                  targetAudience,
                  vibeAndTone,
                  setUploadVideoID,
                  selectedCategory,
                  campaignDescription: effectiveCampaignDescription,
                  ChangeFetchVideoFileStatus,
                  productImageUrl: effectiveProductImageUrl,
                  language,
                  setVideoUrl,
                  setAllGenratedVideos,
                  setSelectedVideo,
                  setCurrentBalance,
                  aspectVideoRatio,
                  setSpinning,
                  getToken,
                  configData,
                  navigate,
                  notify
                });
              } catch (error) {
                notify("Error fetching or uploading video:", "error");
                setSpinning(prev => ({ ...prev, ["video"]: false }));
              }
            };
            fetchAndUpload();
        }
    }, [fetchVideoFileStatus]);

  const checkImage = async (url, name = null, file = null) => {
    if (!name) {
      notify("Please enter a name for the image.", "error");
      setManualLoading(false);
      return;
    }
    const checkImageUrl = (url) => {
        return new Promise((resolve) => {
            // 1. Basic URL check
            if (!url || typeof url !== 'string') {
                return resolve(false);
            }

            const img = new Image();
            
            // Use a timeout to handle requests that hang indefinitely
            const timeoutId = setTimeout(() => {
                img.onload = null;
                img.onerror = null;
                resolve(false);
            }, 2000); // 8-second timeout

            // 2. Success Handler
            img.onload = () => {
                clearTimeout(timeoutId);
                // Optional: Check if the image actually has dimensions (not just a 1x1 tracking pixel)
                if (img.naturalWidth > 0) {
                    resolve(true);
                } else {
                    console.warn(`Image loaded but has no dimensions (0x0): ${url}`);
                    resolve(false);
                }
            };
            // 3. Failure Handler
            img.onerror = (error) => {
                clearTimeout(timeoutId);
                resolve(false);
            };
            img.crossOrigin = null;
            img.src = url;
        });
    };
    const isValid = await checkImageUrl(url);
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timestamp = `${hours}:${minutes}:${seconds}`;
    if (isValid) {
      const bodyData = {
        bannerUrl: url,
        banner_id: name + "_" + timestamp
      };
      setManualUrl("");
      setManualName("");
      setManualFile(null);
      setBannerUrl(url)
      setManualLoading(false);
      setShowManualModal(false);
      uploadToMediaManagement({
        marketingGoals,
        targetAudience,
        vibeAndTone,
        uploadData: {"uploadBannerData": bodyData},
        setUploadAudioID,
        setUploadBannerID,
        setUploadVideoID,
        setAllGenratedBanners,
        getToken,
        configData,
        notify})
    }
    else {
      const looksLikeLocalPath =
        typeof url === "string" &&
        (/^[a-zA-Z]:[\\/]/.test(url) || url.startsWith("\\\\") || url.startsWith("file:"));

      if (file instanceof File) {
        const uploadedUrl = await uploadImageToBucket({
          file,
          name: name + "_" + timestamp || file.name,
          getToken,
          configData,
          notify,
        });

        if (!uploadedUrl) {
          return;
        }

        const bodyData = {
          bannerUrl: uploadedUrl,
          banner_id: name + "_" + timestamp || file.name,
        };

        setManualUrl("");
        setManualName("");
        setManualFile(null);
        setBannerUrl(uploadedUrl);
        setManualLoading(false);
        setShowManualModal(false);
        uploadToMediaManagement({
          marketingGoals,
          targetAudience,
          vibeAndTone,
          uploadData: { uploadBannerData: bodyData },
          setUploadAudioID,
          setUploadBannerID,
          setUploadVideoID,
          setAllGenratedBanners,
          getToken,
          configData,
          notify,
        });
      } else if (looksLikeLocalPath) {
        notify(
          "Local file paths can't be uploaded from the browser. Please choose an image file to upload.",
          "error"
        );
        setManualLoading(false);
      } else {
        notify("Invalid image URL. Please provide a valid image URL.", "error");
        setManualLoading(false);
      }
    }
    return
    }

  const checkVideo = async (url, name = null, file = null) => {
    if (!name) {
      notify("Please enter a name for the video.", "error");
      setManualVideoLoading(false);
      return;
    }

    const isMp4Url = (value) => /\.mp4($|[?#])/i.test(value || "");
    const isMp4File = (value) =>
      value instanceof File &&
      (value.type === "video/mp4" || /\.mp4$/i.test(value.name || ""));

    const checkVideoUrl = (videoSrc) => {
      return new Promise((resolve) => {
        if (!videoSrc || typeof videoSrc !== "string" || !isMp4Url(videoSrc)) {
          return resolve(false);
        }

        const video = document.createElement("video");
        const timeoutId = setTimeout(() => {
          video.onloadedmetadata = null;
          video.onerror = null;
          resolve(false);
        }, 4000);

        video.onloadedmetadata = () => {
          clearTimeout(timeoutId);
          resolve(video.videoWidth > 0);
        };

        video.onerror = () => {
          clearTimeout(timeoutId);
          resolve(false);
        };

        video.preload = "metadata";
        video.src = videoSrc;
      });
    };

    const isValid = await checkVideoUrl(url);
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const timestamp = `${hours}:${minutes}:${seconds}`;

    if (isValid) {
      const bodyData = {
        videoUrl: url,
        video_id: name + "_" + timestamp,
      };

      setManualVideoUrl("");
      setManualVideoName("");
      setManualVideoFile(null);
      setVideoUrl(url);
      setManualVideoLoading(false);
      setShowManualVideoModal(false);

      uploadToMediaManagement({
        marketingGoals,
        targetAudience,
        vibeAndTone,
        uploadData: { uploadVideoData: bodyData },
        setUploadVideoID,
        setAllGenratedVideos,
        setSelectedVideo,
        getToken,
        configData,
        notify,
      });
      return;
    }

    const looksLikeLocalPath =
      typeof url === "string" &&
      (/^[a-zA-Z]:[\\/]/.test(url) || url.startsWith("\\\\") || url.startsWith("file:"));

    if (isMp4File(file)) {
      const uploadedUrl = await uploadImageToBucket({
        file,
        name: name + "_" + timestamp || file.name,
        getToken,
        configData,
        notify,
      });

      if (!uploadedUrl) {
        setManualVideoLoading(false);
        return;
      }

      const bodyData = {
        videoUrl: uploadedUrl,
        video_id: name + "_" + timestamp || file.name,
      };

      setManualVideoUrl("");
      setManualVideoName("");
      setManualVideoFile(null);
      setVideoUrl(uploadedUrl);
      setManualVideoLoading(false);
      setShowManualVideoModal(false);

      uploadToMediaManagement({
        marketingGoals,
        targetAudience,
        vibeAndTone,
        uploadData: { uploadVideoData: bodyData },
        setUploadVideoID,
        setAllGenratedVideos,
        setSelectedVideo,
        getToken,
        configData,
        notify,
      });
      return;
    }

    if (file instanceof File && !isMp4File(file)) {
      notify("Invalid file type. Please choose an MP4 file.", "error");
      setManualVideoLoading(false);
      return;
    }

    if (looksLikeLocalPath) {
      notify(
        "Local file paths can't be uploaded from the browser. Please choose an MP4 file to upload.",
        "error"
      );
      setManualVideoLoading(false);
      return;
    }

    notify("Invalid video URL. Please provide a valid MP4 URL.", "error");
    setManualVideoLoading(false);
  };

  const handleEditBannerSend = async () => {
    setEditBannerLoading(true);

    try {
      const response = await editBannerFile({
        bannerData,
        language,
        aspectBannerRatio,
        bannerUrl,
        editBannerInstructions,
        getToken,
        configData,
      });
      if (response?.banner_url) {
        setBannerUrl(response.banner_url);
      }
      if (response?.current_balance !== undefined) {
        setCurrentBalance(response.current_balance);
      }
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const bodyData = {
        bannerUrl: response.banner_url,
        banner_id: `Banner_${bannerData?.chain_name ?? ""}_${language}_${hours}_${minutes}_${seconds}`,
      };
      uploadToMediaManagement({
        marketingGoals,
        targetAudience,
        vibeAndTone,
        uploadData: { uploadBannerData: bodyData },
        setUploadBannerID,
        setAllGenratedBanners,
        getToken,
        configData,
        notify,
      });
      setShowEditBannerModal(false);
      setEditBannerInstructions("");
      notify("Banner edit request sent successfully.", "success");
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        navigate("/");
      } else {
        notify(error.message || "Failed to edit banner.", "error");
      }
    } finally {
      setEditBannerLoading(false);
    }
  };

return  (
    <FadeIn className="p-8 bg-blue-900 text-white h-full flex flex-col items-center fade-in overflow-y-auto">
      {/* Header */}
      <div className="text-center mb-6 shrink-0">
        <h3 className="text-2xl font-bold mb-2">Generating Assets...</h3>
        <p className="text-blue-200">
          AI is creating visual, audio, and video content simultaneously.
        </p>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl">
        {/* Digital Signage */}
      <AssetCard
        id="signage"
        title="Digital Signage Theme"
        spinning={spinning}
        handleRegenerate={handleRegenerate}
        aspectRatio={aspectBannerRatio}
        setAspectRatio={setAspectBannerRatio}
      >
        {/* Preview */}
        {bannerUrl ? (
          <>
            <div
              onClick={() => setIsOpen(true)}
              className="aspect-video w-full bg-black rounded shadow-inner cursor-pointer overflow-hidden flex items-center justify-center"
            >
              <img
                src={bannerUrl}
                alt="Banner"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Image modal */}
            {isOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
                onClick={() => setIsOpen(false)}
              >
                <button
                  onClick={() => setIsOpen(false)}
                  className="fixed top-4 right-4 z-[60] bg-white text-black rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
                >
                  ✕
                </button>
                <div
                  className="relative max-w-5xl w-full p-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={bannerUrl}
                    alt="Banner preview"
                    className="w-full max-h-[90vh] object-contain rounded"
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-blue-700/50 h-32 rounded flex items-center justify-center mb-3">
            <ImageIcon size={48} className="text-blue-300" />
          </div>
        )}

        {/* ⬇️ ALWAYS VISIBLE CONTROLS */}
        {showManualModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-blue-900 p-6 rounded-xl border border-blue-700 w-96 flex flex-col shadow-lg">
              <h2 className="text-white text-lg font-semibold mb-4">
                Upload Manual Campaign
              </h2>

              <input
                type="text"
                placeholder="Enter image name"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                className="w-full p-2 mb-3 rounded bg-blue-800 text-white border border-blue-700"
              />

              <input
                type="text"
                placeholder="Paste image URL (jpg, jpeg, png)"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                className="w-full p-2 mb-4 rounded bg-blue-800 text-white border border-blue-700"
              />

              <label className="w-full text-sm text-blue-200 mb-2">
                Or choose an image file
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setManualFile(e.target.files?.[0] || null)}
                className="w-full p-2 mb-4 rounded bg-blue-800 text-white border border-blue-700"
              />

              <div className="flex justify-end gap-3">
                <button
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  onClick={() => setShowManualModal(false)}
                >
                  Cancel
                </button>
                <button
                  disabled={manualLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                  onClick={() => {
                    setManualLoading(true);
                    checkImage(manualUrl, manualName , manualFile);
                  }}
                >
                  {manualLoading ? "Loading..." : "Add"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3">
          <select
            value={selectedBanner?.banner_id || ""}
            onChange={(e) => {
              if (e.target.value === "manual") {
                setShowManualModal(true);
              } else {
                const selectedGenratedBanner = allGenratedBanners.find(
                  (b) => b.banner_id === e.target.value
                );
                if (selectedGenratedBanner) {
                  setBannerUrl(selectedGenratedBanner.url);
                  setUploadBannerID(selectedGenratedBanner);
                  setSelectedBanner(selectedGenratedBanner);
                }
              }
            }}
            className="w-full text-sm bg-blue-900 border border-blue-600 rounded p-2 text-white"
          >
            <option value="" disabled>
              Select a previously generated banner
            </option>

            {allGenratedBanners.map((banner) => (
              <option key={banner.banner_id} value={banner.banner_id}>
                {banner.banner_id}
              </option>
            ))}

            <option value="manual">Upload Manual Campaign</option>
          </select>

          <div className="w-full flex gap-2 mt-3">
            <input
              type="checkbox"
              id="animatedBanner"
              checked={templateBanner}
              onChange={(e) => setTemplateBanner(e.target.checked)}
              className="w-4 h-4 accent-blue-400 mt-0.5"
            />
            <label htmlFor="animatedBanner" className="text-sm font-bold text-blue-300">
              Set As Template
            </label>
          </div>
          {bannerUrl && (
            <div className="w-full mt-3">
              <button
                onClick={() => setShowEditBannerModal(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
              >
                Edit Banner
              </button>
            </div>
          )}
        </div>
      </AssetCard>
      {showEditBannerModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setShowEditBannerModal(false)}
        >
          <div
            className="bg-blue-900 border border-blue-700 rounded-xl p-6 w-full max-w-xl shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-white text-lg font-semibold mb-4">Edit Banner</h2>
            <div className="w-full bg-black rounded overflow-hidden mb-4 flex items-center justify-center">
              <img
                src={bannerUrl}
                alt="Banner edit preview"
                className="w-full max-h-[50vh] object-contain"
              />
            </div>
            <textarea
              value={editBannerInstructions}
              onChange={(e) => setEditBannerInstructions(e.target.value)}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              placeholder="Type free text instructions..."
              rows={1}
              className="w-full p-2 mb-4 rounded bg-blue-800 text-white border border-blue-700 resize-none overflow-hidden min-h-[42px]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEditBannerModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
              >
                Close
              </button>
              <button
                onClick={handleEditBannerSend}
                disabled={editBannerLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-500/60 text-white px-4 py-2 rounded"
              >
                {editBannerLoading ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}



        {/* Video */}
        <AssetCard
          id="video"
          title="In-Store TV"
          spinning={spinning}
          handleRegenerate={handleRegenerate}
          aspectVideoRatio={aspectVideoRatio}
          aspectRatio = {aspectVideoRatio}
          setAspectRatio= {setAspectVideoRatio}
        >
            {showManualVideoModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-blue-900 p-6 rounded-xl border border-blue-700 w-96 flex flex-col shadow-lg">
                  <h2 className="text-white text-lg font-semibold mb-4">
                    Upload Manual Video
                  </h2>

                  <input
                    type="text"
                    placeholder="Enter video name"
                    value={manualVideoName}
                    onChange={(e) => setManualVideoName(e.target.value)}
                    className="w-full p-2 mb-3 rounded bg-blue-800 text-white border border-blue-700"
                  />

                  <input
                    type="text"
                    placeholder="Paste MP4 URL"
                    value={manualVideoUrl}
                    onChange={(e) => setManualVideoUrl(e.target.value)}
                    className="w-full p-2 mb-4 rounded bg-blue-800 text-white border border-blue-700"
                  />

                  <label className="w-full text-sm text-blue-200 mb-2">
                    Or choose an MP4 file
                  </label>
                  <input
                    type="file"
                    accept="video/mp4,.mp4"
                    onChange={(e) => setManualVideoFile(e.target.files?.[0] || null)}
                    className="w-full p-2 mb-4 rounded bg-blue-800 text-white border border-blue-700"
                  />

                  <div className="flex justify-end gap-3">
                    <button
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                      onClick={() => setShowManualVideoModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      disabled={manualVideoLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                      onClick={() => {
                        setManualVideoLoading(true);
                        checkVideo(manualVideoUrl, manualVideoName, manualVideoFile);
                      }}
                    >
                      {manualVideoLoading ? "Loading..." : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="aspect-video w-full bg-black rounded overflow-hidden flex items-center justify-center">
              <video
                key={videoUrl || "fallback"}
                className="w-full h-full object-contain"
                controls
                src={
                  videoUrl ??
                  "https://storage.googleapis.com/catch-display/Staging/banners/Muller_Yogurt_Promotion_Clip.mp4"
                }
              />
            </div>
            <div className="mt-3">
              <select
                value={selectedVideo?.video_id || ""}
                onChange={(e) => {
                  if (e.target.value === "manual") {
                    setShowManualVideoModal(true);
                    return;
                  }

                  const getVideoAssetUrl = (video) =>
                    video?.url || video?.videoUrl || video?.video_url || "";

                  const selectedGeneratedVideo = allGenratedVideos.find(
                    (video) => video.video_id === e.target.value
                  );

                  if (selectedGeneratedVideo) {
                    const selectedUrl = getVideoAssetUrl(selectedGeneratedVideo);
                    if (selectedUrl) {
                      setVideoUrl(selectedUrl);
                    }
                    setUploadVideoID(selectedGeneratedVideo);
                    setSelectedVideo(selectedGeneratedVideo);
                  }
                }}
                className="w-full text-sm bg-blue-900 border border-blue-600 rounded p-2 text-white"
              >
                <option value="" disabled>
                  Select a previously generated video
                </option>

                {allGenratedVideos.map((video) => (
                  <option key={video.video_id} value={video.video_id}>
                    {video.video_id}
                  </option>
                ))}
                <option value="manual">Upload Manual Video</option>
              </select>
            </div>
             <div className="w-full flex gap-2 mt-3">
            <input
              type="checkbox"
              id="animatedVideo"
              className="w-4 h-4 accent-blue-400 checked:bg-blue-500 mt-0.5 border border-gray-300 rounded" 
              checked={animateBanner}
              onChange={(e) => setAnimateBanner(e.target.checked)}
            />
            <label htmlFor="animatedVideo" className="text-sm font-bold text-blue-300">
              Animate Banner
            </label>
          </div>
        </AssetCard>

        {/* Audio */}
        <AssetCard
          id="audio"
          title="Audio Spot"
          spinning={spinning}
          handleRegenerate={handleRegenerate}
          setFetchAudioTextStatus={setFetchAudioTextStatus}
          setFetchonlyAudioStatus={setFetchonlyAudioStatus}
        >
          <div className="bg-blue-700/50 rounded mb-3 p-2 flex flex-col gap-2 min-h-32">
            <div className="h-16 flex items-center justify-center">
              {audioUrl ? (
                // Use a 'key' here to force a reload only when the URL actually changes
                <audio key={audioUrl} controls className="w-full h-full">
                  <source src={audioUrl} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              ) : (
                <Volume2 size={48} className="text-blue-300" />
              )}
            </div>
            <div>
              <AutoResizeTextarea
                value={editableTextAudio}
                onChange={setEditableTextAudio}
                onBlur={() => setAudioText(editableTextAudio)}
                placeholder="Audio text will appear here..."
                className="w-full text-xs text-blue-300 italic bg-transparent resize-none outline-none border border-blue-600/40 rounded p-2 overflow-hidden"
              />
            </div>
          </div>

                        <div className="mt-3">
              <select
                value={selectedAudio?.audio_id || ""}
                onChange={(e) => {
                  const selectedGeneratedAudio = allGenratedAudios.find(
                    (audio) => audio.audio_id === e.target.value
                  );

                  if (selectedGeneratedAudio) {
                    const selectedUrl = getAudioAssetUrl(selectedGeneratedAudio);
                    const selectedText = getAudioAssetText(selectedGeneratedAudio);

                    if (selectedUrl) {
                      setAudioUrl(selectedUrl);
                    }
                    setAudioText(selectedText || "");
                    setEditableTextAudio(selectedText || "");
                    setUploadAudioID(selectedGeneratedAudio);
                    setSelectedAudio(selectedGeneratedAudio);
                  }
                }}
                className="w-full text-sm bg-blue-900 border border-blue-600 rounded p-2 text-white"
              >
                <option value="" disabled>
                  Select a previously generated audio
                </option>
                {allGenratedAudios.map((audio) => (
                  <option key={audio.audio_id} value={audio.audio_id}>
                    {audio.audio_id}
                  </option>
                ))}
              </select>
            </div>
        </AssetCard>
      </div>

      {/* Campaign Selection */}
      <div className="w-full max-w-5xl bg-blue-800/50 border border-blue-700 rounded-xl p-5 mt-8 mb-4 backdrop-blur-sm shrink-0">
        <div className="flex flex-col md:flex-row gap-4 md:items-start">
          {/* Predefined Selection */}
          <div className="w-full md:w-1/4">
            <label className="block text-xs font-bold text-blue-300 uppercase mb-1">
              Select Campaign
            </label>
              <div className="relative" ref={campaignDropdownRef}>
                <input
                  type="text"
                  value={campaignQuery}
                  onFocus={() => setIsCampaignDropdownOpen(true)}
                  onChange={(e) => {
                    setCampaignQuery(e.target.value);
                    setIsCampaignDropdownOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setIsCampaignDropdownOpen(false);
                    }
                    if (e.key === "Enter" && filteredCampaignOptions.length > 0) {
                      const selectedOption = filteredCampaignOptions[0];
                      setSelectedCategory(selectedOption.key);
                      setCampaignQuery(selectedOption.label);
                      setIsCampaignDropdownOpen(false);
                    }
                  }}
                  className="w-full text-sm bg-blue-900 border border-blue-600 rounded p-2 text-white placeholder-blue-500 focus:outline-none focus:border-blue-400 shadow-sm"
                  placeholder="Search or select campaign"
                />
                {isCampaignDropdownOpen && (
                  <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded border border-blue-600 bg-blue-900 shadow-lg">
                    {filteredCampaignOptions.length > 0 ? (
                      filteredCampaignOptions.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(option.key);
                            setCampaignQuery(option.label);
                            setIsCampaignDropdownOpen(false);
                          }}
                          className="w-full text-left px-2 py-2 text-sm text-white hover:bg-blue-700"
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <div className="px-2 py-2 text-sm text-blue-300">
                        No campaigns found
                      </div>
                    )}
                  </div>
                )}
              </div>
          </div>

          <div className="hidden md:block text-xs font-bold text-blue-500 pt-8">OR</div>

          {/* Manual Entry */}
          <div className="flex-1 flex gap-3 w-full">
            <div className="w-1/3 flex flex-col">
              <div className="relative mb-1">
                <label className="block text-xs font-bold text-blue-300 uppercase">
                  Product Image URL
                </label>
                <button
                  type="button"
                  onClick={() => productImageFileInputRef.current?.click()}
                  disabled={isUploadingProductImage}
                  title={isUploadingProductImage ? "Uploading image..." : "Upload image"}
                  aria-label={isUploadingProductImage ? "Uploading image..." : "Upload image"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-blue-300 hover:text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <Upload size={16} className={isUploadingProductImage ? "animate-spin" : ""} />
                </button>
              </div>
              <div>
                <input
                  type="text"
                  value={productImageUrl}
                  onChange={(e) => {
                    setProductImageUrl(e.target.value);
                    setSelectedCategory("manual");
                    setCampaignQuery("Custom / Manual");
                  }}
                  className="w-full text-sm bg-blue-900 border border-blue-600 rounded p-2 text-white placeholder-blue-500 focus:outline-none focus:border-blue-400 shadow-sm"
                  placeholder="https://..."
                />
                <input
                  ref={productImageFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const selectedFile = e.target.files?.[0];
                    await handleProductImageUpload(selectedFile);
                    e.target.value = "";
                  }}
                />
              </div>
              {normalizedProductImageUrl && (
                <div className="mt-2 rounded border border-blue-700 bg-blue-950/50 p-2">
                  <img
                    src={normalizedProductImageUrl}
                    alt="Product preview"
                    onLoad={() => setIsProductImagePreviewValid(true)}
                    onError={() => setIsProductImagePreviewValid(false)}
                    className={`h-24 w-full rounded object-contain ${
                      isProductImagePreviewValid ? "block" : "hidden"
                    }`}
                  />
                  {!isProductImagePreviewValid && (
                    <p className="text-xs text-blue-300">Enter a valid image URL to preview.</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col">
              <label className="block text-xs font-bold text-blue-300 uppercase mb-1">
                Campaign Description
              </label>
              <textarea
                value={campaignDescription || ""}
                onChange={(e) => {
                  setCampaignDescription(e.target.value);
                  setSelectedCategory("manual");
                  setCampaignQuery("Custom / Manual");
                }}
                className={`w-full ${campaignDescriptionHeightClass} ${campaignDescriptionFontClass} text-center bg-blue-900 border border-blue-600 rounded p-2 text-white placeholder-blue-500 focus:outline-none focus:border-blue-400 shadow-sm resize-none overflow-y-auto`}
                placeholder="Campaign description..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Deploy Button */}
      <div className="mt-4 pt-6 border-t border-blue-800 w-full max-w-5xl flex justify-center">
        <button
          onClick={
            () => {
              onDeploy();
            }
          }
          className="bg-blue-500 hover:bg-blue-400 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2"
        >
          <span>Proceed to Deploy</span>
          <ArrowRight size={20} />
        </button>
      </div>
    </FadeIn>
  );
}